import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
// `react18-input-otp` ships its bundle as CommonJS. Depending on the bundler
// being used by the host application the default import may resolve to the
// module **itself** (an object) instead of the component function that lives
// on `module.default`. Rendering that object makes React throw the familiar
// "Element type is invalid" error.  To be resilient across build systems we
// detect that situation at runtime and gracefully pick `module.default` when
// it exists.

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – the package ships no types, we rely on our local declaration
import OtpInputCjs from 'react18-input-otp';

// The actual React component can live on the default export or be the export
// itself.  Normalise it so the rest of the component can use it safely.
// Using `any` to avoid battles with types from the untyped dependency.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const OtpInput: any = (OtpInputCjs as any)?.default ?? (OtpInputCjs as any);

// Length of a standard TOTP code (RFC 6238) – 6 digits.
export const DEFAULT_CODE_LENGTH = 6;

export interface TOTPInputProps {
  // eslint-disable-next-line noCommentedCode/no-commented-code
  /** Optional additional class names applied to input container */
  className?: string;
  /** When present, a hidden input with this name will receive the OTP value so regular form submits still work */
  name?: string;
  /** Controlled value.  If omitted the component is uncontrolled */
  value?: string;
  /** Disable user interaction */
  disabled?: boolean;
  /** Mark the hidden input as required */
  required?: boolean;
  /** Called whenever the value changes */
  onChange?: (value: string) => void;
}

/**
 * A 6-digit, numeric OTP input used for software-token MFA (TOTP).
 * Wraps the lightweight `react18-input-otp` component and applies Elements theme styling.
 */
export const TOTPInput: FC<TOTPInputProps> = ({ className, name, value, disabled, required, onChange }) => {
  const [code, setCode] = useState(value || '');

  // Keep the caller in sync when the local state changes
  useEffect(() => {
    onChange?.(code);
  }, [code, onChange]);

  return (
    <>
      {/* Hidden field so Remix / regular forms pick up the value on submit */}
      {name && <input type="hidden" name={name} value={code} />}

      <OtpInput
        isInputNum
        value={code}
        shouldAutoFocus
        onChange={setCode}
        numInputs={DEFAULT_CODE_LENGTH}
        isDisabled={disabled}
        inputProps={{
          required: required ?? true,
          pattern: '[0-9]',
        }}
        containerStyle={`${className ?? ''} justify-center flex space-x-4`}
        inputStyle={clsx(
          'h-[88px] !w-14 appearance-none caret-transparent text-white',
          'bg-darkMode-gray-medium20 rounded-md border-none p-0',
          'text-center align-middle text-6xl font-bold',
          'focus:border-2 focus:border-solid focus:border-palette-600 focus:bg-palette-600/20',
          'valid:bg-palette-600 valid:bg-opacity-10'
        )}
      />
    </>
  );
};
